import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Typography,
  Grid,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import LocalStorageHandler from "../../utils/LocalStorageHandler";

import {
  autocompleteStyle,
  buttonStyle,
  RELATIONSHIPOPTIONS,
  themeStyles,
} from "../../constants";
import { useDispatch } from "react-redux";

export default function DialogForm({
  open,
  handleClose,
  handleSubmit,
  isEditMode,
  rowData,
  fetchedClients,
}) {
  const [formState, setFormState] = useState({
    id: null,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    relationship: "",
    status: "Active",
    digitalSignature: false,
  });

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    relationship: false,
    digitalSignature: false,
  });

  const [filteredClients, setFilteredClients] = useState([]);
  const dispatch = useDispatch();
  const userEmail = LocalStorageHandler.getItem("username"); // Get logged-in user's email
  const [selectedClient, setSelectedClient] = useState({});
  const [isSingleClient, setIsSingleClient] = useState(false);
  useEffect(() => {
    console.log("fetched in dialog", fetchedClients);
    let matchingClients = [];
    if (fetchedClients?.length > 0) {
      matchingClients = fetchedClients.filter((client) => {
        console.log("hi", client);
        return client.email === userEmail;
      });
      setFilteredClients(matchingClients);
    }
    if (isEditMode && rowData) {
      setSelectedClient({
        id: rowData?.clientId,
        lastName: rowData?.clientLastName,
        firstName: rowData.clientFirstName,
      });
      if (matchingClients.length === 1) {
        setIsSingleClient(true);
      }
      console.log("edit mode");
      setFormState(rowData);
    } else {
      if (matchingClients.length === 1) {
        setSelectedClient(matchingClients[0]);
        setIsSingleClient(true);
        setFormState((prevState) => ({
          id: null,
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          relationship: "",
          status: "Active",
          digitalSignature: false,
          client: { id: matchingClients[0].id },
          clientFirstName: matchingClients[0].firstName,
          clientLastName: matchingClients[0].lastName,
          clientId: matchingClients[0].id,
        }));
      }
    }

    setErrors({
      firstName: false,
      lastName: false,
      email: false,
      phone: false,
      relationship: false,
      digitalSignature: false,
    });
  }, [isEditMode, rowData, fetchedClients, userEmail]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setErrors((prevState) => ({ ...prevState, [name]: false }));
  };

  const handleRelationChange = (event, newValue) => {
    setFormState((prevState) => ({
      ...prevState,
      relationship: newValue || "",
    }));
    setErrors((prevState) => ({ ...prevState, relationship: false }));
  };

  const handleCheckboxChange = (e) => {
    setFormState((prevState) => ({
      ...prevState,
      digitalSignature: e.target.checked,
    }));
    setErrors((prevState) => ({ ...prevState, digitalSignature: false }));
  };

  // Email validation regex
  const emailValidator = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation regex (allows digits, spaces, dashes, parentheses)
  const phoneValidator = (phone) => {
    const phoneRegex = /^[0-9\-\(\)\s]+$/;
    return phoneRegex.test(phone) && phone.length >= 10 && phone.length <= 15; // Assuming a minimum of 10 digits
  };

  const validateForm = () => {
    const newErrors = {
      firstName: !formState.firstName,
      lastName: !formState.lastName,
      email: !formState.email || !emailValidator(formState.email), // Email validation
      phone: !formState.phone || !phoneValidator(formState.phone), // Phone validation
      relationship: !formState.relationship,
      digitalSignature: !formState.digitalSignature,
    };

    setErrors(newErrors);

    return (
      !newErrors.firstName &&
      !newErrors.lastName &&
      !newErrors.email &&
      !newErrors.phone &&
      !newErrors.relationship &&
      !newErrors.digitalSignature
    );
  };

  const handleFormSubmit = () => {
    console.log(formState, "saving");
    if (validateForm()) {
      handleSubmit(formState);
    }
  };

  const handleActivate = () => {
    setFormState((prevState) => ({
      ...prevState,
      status: "Active",
    }));
    console.log(formState, "saving");
    handleSubmit(formState, "reactivate");
  };
  const handleClientSelect = (event, newValue) => {
    if (newValue) {
      setFormState((prevState) => ({
        ...prevState,
        client: { id: newValue.id },
        clientFirstName: newValue.firstName,
        clientLastName: newValue.lastName,
        clientId: newValue.id,
      }));
    }
    setSelectedClient(newValue);
  };
  const isInactive = formState.status === "Inactive";
  console.log(selectedClient, "selected client");
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h5" fontWeight="bold" color="primary">
          {isEditMode ? "Edit Details" : "Add Details"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ marginTop: 2 }}>
          <Grid item xs={12}>
            <Autocomplete
              options={filteredClients}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName} `
              }
              value={selectedClient}
              onChange={handleClientSelect}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Learner"
                  sx={{ ...themeStyles.autocompleteStyles }}
                  fullWidth
                  required
                />
              )}
              disabled={isInactive || isSingleClient}
            />
          </Grid>
          {/* First Name and Last Name in a row */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formState.firstName}
              onChange={handleInputChange}
              required
              error={errors.firstName}
              helperText={errors.firstName && "First Name is required"}
              disabled={isInactive}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formState.lastName}
              onChange={handleInputChange}
              required
              error={errors.lastName}
              helperText={errors.lastName && "Last Name is required"}
              disabled={isInactive}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={formState.email}
              onChange={handleInputChange}
              required
              error={errors.email}
              helperText={errors.email ? "Valid Email is required" : ""}
              disabled={isInactive}
            />
          </Grid>

          {/* Phone Number */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formState.phone}
              onChange={handleInputChange}
              required
              error={errors.phone}
              helperText={
                errors.phone
                  ? "Valid phone number is required (digits, dashes, spaces)"
                  : ""
              }
              disabled={isInactive}
            />
          </Grid>

          {/* Relationship */}
          <Grid item xs={12}>
            <Autocomplete
              options={RELATIONSHIPOPTIONS}
              value={formState.relationship}
              onChange={handleRelationChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Relationship"
                  required
                  error={errors.relationship}
                  helperText={errors.relationship && "Relationship is required"}
                  disabled={isInactive}
                />
              )}
            />
          </Grid>

          {/* Digital Signature and Agreement */}
          <Grid item xs={12}>
            <Typography
              sx={{
                height: "150px",
                overflowY: "auto",
                border: "1px solid #ccc",
                padding: "10px",
                marginBottom: "10px",
              }}>
              I, as a care giver of{" "}
              <b>
                {selectedClient.firstName} {selectedClient.lastName}
              </b>
              , authorize{" "}
              <b>
                {formState.firstName} {formState.lastName}
              </b>{" "}
              to access{" "}
              <b>
                {selectedClient.firstName} {selectedClient.lastName}'s
              </b>{" "}
              Protected Health Information (PHI) maintained by Quality ABA
              Services [e.g., progress report, treatment plan, therapy
              evaluation, session data etc..]. By selecting <b>“I Agree”</b>, I
              acknowledge that This consent serves as my digital signature and
              is legally binding. This consent remains valid until revoked.{" "}
              <br />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formState.digitalSignature}
                    onChange={handleCheckboxChange}
                    sx={{
                      "&.Mui-checked": { color: "green" },
                    }}
                    disabled={isInactive}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontWeight: "bold",
                    }}>
                    I agree
                  </Typography>
                }
              />
              <Typography sx={{ color: "red", fontWeight: "bold" }}>
                This is equivalent to a digital signature
              </Typography>
              {errors.digitalSignature && (
                <Typography sx={{ color: "red" }}>
                  You must agree to the terms and conditions before saving.
                </Typography>
              )}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          sx={{
            ...buttonStyle,
            backgroundColor: "red",
            color: "white",
            "&:hover": {
              backgroundColor: "darkred",
            },
          }}>
          Cancel
        </Button>
        {isEditMode && isInactive ? (
          <Button
            onClick={handleActivate}
            sx={{
              ...buttonStyle,
              backgroundColor: "blue",
              color: "white",

              "&:hover": {
                backgroundColor: "darkblue",
              },
            }}>
            Activate
          </Button>
        ) : (
          <Button
            onClick={handleFormSubmit}
            sx={{
              ...buttonStyle,
              backgroundColor: "blue",
              color: "white",
              "&:hover": {
                backgroundColor: "darkblue",
              },
            }}
            disabled={isInactive}>
            {isEditMode ? "Update" : "Save"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
